import { invoke, map, times, uniqueId } from 'lodash';
import { getImagesArray } from '../../../../hooks/useImagesArray';
import { IDCARD_SECTIONS_ENUM } from './constants';

export const createComparisonCut = ({ isHeader, senderMetadata }) => ({
  id: uniqueId('comparison-cut-'),
  isHeader,
  senderMetadata,
});

export const getCatalogItemMetadata = ({ sectionId, isCollapsible, isCollapsed, comparisonCuts }) => ({
  sectionId,
  isCollapsible,
  isCollapsed,
  comparisonCuts: comparisonCuts || [],
});

export const getSectionsMetadata = ({ firstSlotCatalogItem, secondSlotCatalogItem }) => {
  const { DocumentList, EquipmentList, OtherDocumentList, TieIns, ViewsAndLayouts, WeightBreakdown, LocationOnFPSO } =
    IDCARD_SECTIONS_ENUM;

  const defaultConfigCollapsables = {
    isCollapsible: true,
    isCollapsed: true,
    comparisonCuts: [],
  };

  const staticSenderMetadata = {
    left: {
      idCard: firstSlotCatalogItem?.idCard,
    },
    right: {
      idCard: secondSlotCatalogItem?.idCard,
    },
  };

  const defaultConfigNonCollapsables = {
    ...defaultConfigCollapsables,
    isCollapsible: false,
    comparisonCuts: [
      createComparisonCut({
        id: 'comparison-cut-1',
        isHeader: true,
        senderMetadata: staticSenderMetadata,
      }),
    ],
  };

  return {
    [ViewsAndLayouts]: getCatalogItemMetadata({ sectionId: ViewsAndLayouts, ...defaultConfigCollapsables }),
    [WeightBreakdown]: getCatalogItemMetadata({
      sectionId: WeightBreakdown,
      ...defaultConfigNonCollapsables,
    }),
    [TieIns]: getCatalogItemMetadata({
      sectionId: TieIns,
      ...defaultConfigNonCollapsables,
    }),
    [EquipmentList]: getCatalogItemMetadata({
      sectionId: EquipmentList,
      ...defaultConfigNonCollapsables,
    }),
    [DocumentList]: getCatalogItemMetadata({
      sectionId: DocumentList,
      ...defaultConfigNonCollapsables,
    }),
    [OtherDocumentList]: getCatalogItemMetadata({
      sectionId: OtherDocumentList,
      ...defaultConfigNonCollapsables,
    }),
    [LocationOnFPSO]: getCatalogItemMetadata({ sectionId: LocationOnFPSO, ...defaultConfigCollapsables }),
  };
};

const calculateComparisonCutForViewsAndLayouts = ({ firstSlotCatalogItem, secondSlotCatalogItem }) => {
  const { imageArray: leftSlotImages } = getImagesArray(firstSlotCatalogItem?.idCard?.images || [], 7);
  const { imageArray: rightSlotImages } = getImagesArray(secondSlotCatalogItem?.idCard?.images || [], 7);

  const nComparisonCuts = Math.min(leftSlotImages.length, rightSlotImages.length);

  const comparisonCuts = map(times(nComparisonCuts), (nth) =>
    createComparisonCut({
      isHeader: false,
      senderMetadata: {
        left: {
          idCard: firstSlotCatalogItem?.idCard,
          image: leftSlotImages[nth],
        },
        right: {
          idCard: secondSlotCatalogItem?.idCard,
          image: rightSlotImages[nth],
        },
      },
    })
  );

  return comparisonCuts;
};

const calculateComparisonCutsForLocationAndCraneCoverage = ({ firstSlotCatalogItem, secondSlotCatalogItem }) => {
  const { imageArray: allLeftSlotImages } = getImagesArray(firstSlotCatalogItem?.idCard?.images || [], 10);
  const { imageArray: allRightSlotImages } = getImagesArray(secondSlotCatalogItem?.idCard?.images || [], 10);

  const leftSlotImages = allLeftSlotImages.slice(7);
  const rightSlotImages = allRightSlotImages.slice(7);

  const nComparisonCuts = Math.min(leftSlotImages.length, rightSlotImages.length);

  const comparisonCuts = map(times(nComparisonCuts), (nth) =>
    createComparisonCut({
      isHeader: false,
      senderMetadata: {
        left: {
          idCard: firstSlotCatalogItem?.idCard,
          image: leftSlotImages[nth],
        },
        right: {
          idCard: secondSlotCatalogItem?.idCard,
          image: rightSlotImages[nth],
        },
      },
    })
  );

  return comparisonCuts;
};

export const calculateComparisonCuts = ({ sectionId, firstSlotCatalogItem, secondSlotCatalogItem }) => {
  const calculationChart = {
    [IDCARD_SECTIONS_ENUM.ViewsAndLayouts]: (payload) => calculateComparisonCutForViewsAndLayouts(payload),
    [IDCARD_SECTIONS_ENUM.LocationOnFPSO]: (payload) => calculateComparisonCutsForLocationAndCraneCoverage(payload),
  };

  const result = invoke(calculationChart, sectionId, {
    firstSlotCatalogItem,
    secondSlotCatalogItem,
  });

  return result;
};
